
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Helper function to convert timestamps to ISO strings (backend handles this now)
 * @param {Object} data - Data object
 * @returns {Object} Data with timestamps as expected
 */
const processTimestamps = (data) => {
  return {
    ...data,
    // Backend returns ISO strings, so no conversion needed
  };
};

/**
 * Secure Surgical Procedure Mapping service
 * Uses backend API endpoints instead of direct Firestore access
 */
export const surgicalProcedureMappingService = {
  /**
   * Get all surgical procedure mappings for a facility
   * @param {string} facilityId - Facility ID
   * @param {number} page - Page number for pagination
   * @param {number} limit - Number of items per page
   * @returns {Promise<Object>} Promise resolving to paginated procedure mappings
   */
  getAllProcedureMappings: async (facilityId, page = 1, limit = 50) => {
    try {
      console.log('Fetching surgical procedure mappings for facility:', facilityId);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('facilityId', facilityId);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const response = await axios.get(
        `${API_BASE}/surgical/procedure-mappings?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Process the data to ensure timestamps are in expected format
      const mappings = response.data.procedureMappings.map(processTimestamps);
      
      return {
        procedureMappings: mappings,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error fetching surgical procedure mappings:', error);
      throw error;
    }
  },

  /**
   * Get a specific surgical procedure mapping by ID
   * @param {string} mappingId - Mapping ID
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to procedure mapping
   */
  getProcedureMappingById: async (mappingId, facilityId) => {
    try {
      console.log('Fetching surgical procedure mapping:', mappingId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/surgical/procedure-mappings/${mappingId}?facilityId=${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return processTimestamps(response.data.procedureMapping);
    } catch (error) {
      console.error('Error fetching surgical procedure mapping by ID:', error);
      throw error;
    }
  },

  /**
   * Create a new surgical procedure mapping
   * @param {Object} mappingData - Mapping data
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to created mapping
   */
  createProcedureMapping: async (mappingData, facilityId) => {
    try {
      console.log('Creating surgical procedure mapping for facility:', facilityId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/surgical/procedure-mappings`,
        {
          ...mappingData,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return processTimestamps(response.data);
    } catch (error) {
      console.error('Error creating surgical procedure mapping:', error);
      throw error;
    }
  },

  /**
   * Update an existing surgical procedure mapping
   * @param {string} mappingId - Mapping ID
   * @param {Object} updateData - Data to update
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to update result
   */
  updateProcedureMapping: async (mappingId, updateData, facilityId) => {
    try {
      console.log('Updating surgical procedure mapping:', mappingId);
      const token = await getAuthToken();
      
      const response = await axios.put(
        `${API_BASE}/surgical/procedure-mappings/${mappingId}`,
        {
          ...updateData,
          facilityId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error updating surgical procedure mapping:', error);
      throw error;
    }
  },

  /**
   * Delete a surgical procedure mapping
   * @param {string} mappingId - Mapping ID
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to deletion result
   */
  deleteProcedureMapping: async (mappingId, facilityId) => {
    try {
      console.log('Deleting surgical procedure mapping:', mappingId);
      const token = await getAuthToken();
      
      const response = await axios.delete(
        `${API_BASE}/surgical/procedure-mappings/${mappingId}?facilityId=${facilityId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data;
    } catch (error) {
      console.error('Error deleting surgical procedure mapping:', error);
      throw error;
    }
  },

  /**
   * Search procedure mappings by CPT code
   * @param {string} cptCode - CPT code to search for
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array>} Promise resolving to matching mappings
   */
  searchByCptCode: async (cptCode, facilityId) => {
    try {
      console.log('Searching procedure mappings by CPT code:', cptCode);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('facilityId', facilityId);
      params.append('cptCode', cptCode);
      
      const response = await axios.get(
        `${API_BASE}/surgical/procedure-mappings/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.procedureMappings.map(processTimestamps);
    } catch (error) {
      console.error('Error searching procedure mappings by CPT code:', error);
      throw error;
    }
  },

  /**
   * Get procedure mappings by category
   * @param {string} category - Category to filter by
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Array>} Promise resolving to filtered mappings
   */
  getProcedureMappingsByCategory: async (category, facilityId) => {
    try {
      console.log('Fetching procedure mappings by category:', category);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('facilityId', facilityId);
      params.append('category', category);
      
      const response = await axios.get(
        `${API_BASE}/surgical/procedure-mappings?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.procedureMappings.map(processTimestamps);
    } catch (error) {
      console.error('Error fetching procedure mappings by category:', error);
      throw error;
    }
  },

  /**
   * Validate a CPT code exists in mappings
   * @param {string} cptCode - CPT code to validate
   * @param {string} facilityId - Facility ID
   * @returns {Promise<boolean>} Promise resolving to validation result
   */
  validateCptCode: async (cptCode, facilityId) => {
    try {
      const mappings = await surgicalProcedureMappingService.searchByCptCode(cptCode, facilityId);
      return mappings.length > 0;
    } catch (error) {
      console.error('Error validating CPT code:', error);
      return false;
    }
  },

  /**
   * Get procedure mapping statistics for a facility
   * @param {string} facilityId - Facility ID
   * @returns {Promise<Object>} Promise resolving to statistics
   */
  getProcedureMappingStats: async (facilityId) => {
    try {
      console.log('Fetching procedure mapping statistics for facility:', facilityId);
      
      // Get all procedure mappings for the facility and calculate stats
      const allMappings = await surgicalProcedureMappingService.getAllProcedureMappings(facilityId);
      
      // Calculate statistics
      const stats = {
        totalMappings: allMappings.length,
        byBodySystem: allMappings.reduce((acc, m) => {
          const bodySystem = m.bodySystem || 'Uncategorized';
          acc[bodySystem] = (acc[bodySystem] || 0) + 1;
          return acc;
        }, {}),
        byApproach: allMappings.reduce((acc, m) => {
          const approach = m.approach || 'Uncategorized';
          acc[approach] = (acc[approach] || 0) + 1;
          return acc;
        }, {}),
        averageDuration: allMappings.length > 0 
          ? allMappings.reduce((sum, m) => sum + (m.defaultDuration || 0), 0) / allMappings.length 
          : 0,
        totalDuration: allMappings.reduce((sum, m) => sum + (m.defaultDuration || 0), 0)
      };
      
      return stats;
    } catch (error) {
      console.error('Error fetching procedure mapping statistics:', error);
      throw error;
    }
  }
};
